package xyz.luan.bpc.parser

import xyz.luan.bpc.Centre
import xyz.luan.bpc.Circle
import xyz.luan.bpc.Problem
import xyz.luan.bpc.Step
import xyz.luan.bpc.operation.CentreOperation
import xyz.luan.bpc.operation.MarkingOperation
import xyz.luan.bpc.operation.StepOperation

object Parser {
    fun parse(circles: List<String>, centre: String = "", markings: String = ""): Problem {
        return Problem(
            circles = circles.mapIndexedNotNull { index, value ->
                parseCircle(ParsingContext(name = "c$index"), value)
            },
            centre = parseCentre(ParsingContext(name = "centre"), centre),
            markings = parseMarkings(ParsingContext(name = "markings"), markings),
        )
    }

    fun parseCentre(context: ParsingContext, input: String): Centre? {
        if (input.isEmpty()) {
            return null
        }
        val parts = input.split(" ")
        if (parts.size < 2) {
            context.fail("Invalid centre input: $input (needs two elements)")
        }
        return Centre(
            applyTo = StepOperation.parse(context, parts.first()),
            operations = parts.drop(1).map { CentreOperation.parse(context, it) },
        )
    }

    fun parseMarkings(context: ParsingContext, input: String): Map<Double, MarkingOperation> {
        if (input.isEmpty()) {
            return mapOf()
        }

        return input.split(" ").associate {
            val parts = it.split(":")
            if (parts.size != 2) {
                context.fail("Invalid marking input: $it (needs two elements)")
            }
            val key = parseNumber(context, parts[0])
            val value = MarkingOperation.parse(context, parts[1])
            key to value
        }
    }

    fun parseDouble(context: ParsingContext, input: String): Double {
        return input.trim().toDoubleOrNull()
            ?: context.fail("Invalid numeric input: $input on ${context.name}")
    }

    fun parseNumber(context: ParsingContext, input: String): Double {
        val segments = input.split('/')
        return when (segments.size) {
            0 -> context.fail("Invalid numeric input: (empty) on ${context.name}")
            1 -> parseDouble(context, input)
            2 -> {
                val num = parseDouble(context, segments[0])
                val den = parseDouble(context, segments[1])
                if (den == 0.0) {
                    context.fail("Division by zero in fraction: $input on ${context.name}")
                }
                num / den
            }
            else -> context.fail("Invalid numeric input: $input on ${context.name}")
        }
    }

    fun parseCircle(context: ParsingContext, input: String): Circle? {
        if (input.isEmpty()) {
            return null
        }
        val steps = input.split(" ").map { parseInput(context, it) }
        return Circle(steps = steps)
    }

    fun parseInput(context: ParsingContext, input: String): Step {
        for (op in StepOperation.entries) {
            if (input.startsWith(op.symbol)) {
                return Step(op, parseNumber(context, input.removePrefix(op.symbol)))
            }
        }
        return Step(StepOperation.PLUS, parseNumber(context, input))
    }
}
